/**
 * @file catalyst-inner-embed.tsx
 * @desc Thin adapter that renders the Catalyst widget inside the embed bundle.
 *
 * The embed entry point (agent.tsx) already wraps with ConversationProvider,
 * so we import the default Catalyst export which itself wraps CatalystInner
 * with a second ConversationProvider. Nesting providers is safe in
 * @elevenlabs/react -- the inner one wins. However to be clean we use the
 * main Catalyst export directly, which wraps both providers correctly.
 *
 * The embed cannot use Next.js server components or 'use client' directives --
 * this file is compiled by esbuild into the standalone IIFE bundle. Removing
 * the 'use client' directive is correct here: esbuild handles the bundle
 * as pure browser code, not SSR.
 *
 * Props mirror the extended CatalystProps with languages and modes added in Phase 1.
 */

import Catalyst from '../components/Catalyst'
import type { ResolvedBrand } from '../lib/brand/resolveBrand'

/**
 * Props for the embed adapter.
 * All fields forwarded directly to the main Catalyst component.
 */
interface CatalystInnerEmbedProps {
  /** Synthesised brand config built from the config API response in agent.tsx. */
  brand: ResolvedBrand
  /**
   * ISO 639-1 codes for supported languages.
   * When multiple codes are provided, a dropdown appears in the widget header.
   */
  languages: string[]
  /**
   * Enabled interaction modes.
   * 'voice' -- voice call via ElevenLabs WebRTC
   * 'text'  -- text chat via Groq streaming
   */
  modes: ('voice' | 'text')[]
  /**
   * When true, suppress the collapsed floating launcher. The widget still mounts
   * and opens via catalyst:open / catalyst:open-voice — for host pages that drive
   * it from their own CTA. Set by data-hide-launcher on the embed script tag.
   */
  hideLauncher?: boolean
}

/**
 * Renders the full Catalyst widget with language and mode configuration.
 *
 * This component is the leaf React component inside the embed bundle's shadow DOM.
 * It passes all configuration down to the existing Catalyst.tsx implementation
 * so the embed widget looks and behaves identically to the in-app widget.
 */
export default function CatalystInnerEmbed({ brand, languages, modes, hideLauncher }: CatalystInnerEmbedProps) {
  return (
    <Catalyst
      brand={brand}
      languages={languages}
      modes={modes}
      hideLauncher={hideLauncher}
    />
  )
}
